/**
 * DSAP Mock Implementation
 * Browser-side DSAP simulator that implements all 5 core methods
 * Based on DSAP v1.0 protocol specification
 */

// Type definitions based on DSAP schemas
export interface DSAPRequest {
  jsonrpc: '2.0'
  method: string
  params: any
  id: string | number
}

export interface DSAPResponse {
  jsonrpc: '2.0'
  result?: any
  error?: DSAPError
  id: string | number
}

export interface DSAPError {
  code: number
  message: string
  data?: {
    dsapErrorCode: string
    reason: string
    resource?: string
    userId?: string
    auditRecordId?: string
    retryAllowed?: boolean
    helpUrl?: string
    context?: any
    recommendations?: string[]
  }
}

export interface AgentCard {
  dsapVersion: string
  agentCard: {
    name: string
    description: string
    vendor: string
    version: string
    logoUrl?: string
    capabilities: {
      accessVerification: {
        enabled: boolean
        responseTime: string
        supportedOperations: string[]
      }
      complianceChecking: {
        enabled: boolean
        supportedRegulations: string[]
        riskAssessment: boolean
      }
      auditLogging: {
        enabled: boolean
        retentionPeriod: string
        realTimeStreaming: boolean
      }
      dataClassification: {
        enabled: boolean
        autoClassification: boolean
      }
    }
    authentication: {
      schemes: string[]
    }
    endpoints: {
      base: string
      jsonrpc: string
    }
    supportedRegulations: string[]
  }
}

export interface Scenario {
  id: string
  name: string
  description: string
  agentId: string
  userId: string
  dataResource: string
  operation: string
  context: {
    businessPurpose: string
    requestedFields: string[]
    sensitivity: string[]
  }
  expectedOutcome: 'granted' | 'denied' | 'partial'
  expectedRiskScore: number
}

// Mock data and scenarios
export const MOCK_SCENARIOS: Scenario[] = [
  {
    id: 'hr-employee-access',
    name: 'HR Copilot - Employee Data Access',
    description: 'HR assistant accessing employee records for routine inquiries',
    agentId: 'hr-copilot-v2.1',
    userId: 'jane.doe@acmecorp.com',
    dataResource: 'database://hr/employee-records',
    operation: 'read',
    context: {
      businessPurpose: 'employee_inquiry_support',
      requestedFields: ['name', 'email', 'department', 'manager', 'start_date'],
      sensitivity: ['PII', 'internal']
    },
    expectedOutcome: 'granted',
    expectedRiskScore: 25
  },
  {
    id: 'sales-crm-denied',
    name: 'Sales Agent - CRM Access (Denied SSN)',
    description: 'Sales agent trying to access customer data including sensitive fields',
    agentId: 'sales-agent-v1.5',
    userId: 'john.smith@acmecorp.com',
    dataResource: 'crm://salesforce/customer-records',
    operation: 'read',
    context: {
      businessPurpose: 'lead_qualification',
      requestedFields: ['name', 'email', 'company', 'ssn', 'credit_score'],
      sensitivity: ['PII', 'financial']
    },
    expectedOutcome: 'partial',
    expectedRiskScore: 65
  },
  {
    id: 'finance-payroll-high-risk',
    name: 'Finance Bot - Payroll Data (High Risk)',
    description: 'Finance automation bot accessing payroll for reporting',
    agentId: 'finance-bot-v3.0',
    userId: 'finance.admin@acmecorp.com',
    dataResource: 'database://payroll/salary-data',
    operation: 'read',
    context: {
      businessPurpose: 'monthly_payroll_report',
      requestedFields: ['employee_id', 'salary', 'bonus', 'deductions'],
      sensitivity: ['financial', 'confidential']
    },
    expectedOutcome: 'granted',
    expectedRiskScore: 85
  }
]

// Mock user roles and permissions
const MOCK_USER_ROLES: Record<string, string[]> = {
  'jane.doe@acmecorp.com': ['hr_manager', 'employee_data_access'],
  'john.smith@acmecorp.com': ['sales_rep', 'customer_contact_access'],
  'finance.admin@acmecorp.com': ['finance_admin', 'payroll_access', 'financial_reporting']
}

// Mock audit log entries
const auditLog: Array<{
  id: string
  timestamp: string
  agentId: string
  userId: string
  resource: string
  operation: string
  outcome: string
  riskScore: number
}> = []

/**
 * DSAP Mock Server Implementation
 */
export class DSAPMockServer {
  private agentCard: AgentCard = {
    dsapVersion: '1.0',
    agentCard: {
      name: 'Netwrix Data Security Gateway',
      description: 'Enterprise DSAP server providing real-time data access control and compliance automation',
      vendor: 'Netwrix Corporation',
      version: '2.1.0',
      logoUrl: '/dsap-logo.svg',
      capabilities: {
        accessVerification: {
          enabled: true,
          responseTime: '< 100ms',
          supportedOperations: ['read', 'write', 'create', 'update', 'delete']
        },
        complianceChecking: {
          enabled: true,
          supportedRegulations: ['GDPR', 'CCPA', 'HIPAA', 'SOX', 'PCI_DSS'],
          riskAssessment: true
        },
        auditLogging: {
          enabled: true,
          retentionPeriod: 'P7Y', // 7 years
          realTimeStreaming: true
        },
        dataClassification: {
          enabled: true,
          autoClassification: true
        }
      },
      authentication: {
        schemes: ['oauth2', 'bearer']
      },
      endpoints: {
        base: 'https://dsap.acmecorp.com',
        jsonrpc: 'https://dsap.acmecorp.com/v1/rpc'
      },
      supportedRegulations: ['GDPR', 'CCPA', 'HIPAA', 'SOX', 'PCI_DSS', 'GLBA']
    }
  }

  /**
   * Process DSAP JSON-RPC request
   */
  async processRequest(request: DSAPRequest): Promise<DSAPResponse> {
    // Simulate network latency
    await new Promise(resolve => setTimeout(resolve, Math.random() * 200 + 50))

    switch (request.method) {
      case 'dsap.discover':
        return this.handleDiscover(request)
      case 'dsap.verifyAccess':
        return this.handleVerifyAccess(request)
      case 'dsap.logActivity':
        return this.handleLogActivity(request)
      case 'dsap.checkCompliance':
        return this.handleCheckCompliance(request)
      case 'dsap.classifyData':
        return this.handleClassifyData(request)
      default:
        return {
          jsonrpc: '2.0',
          error: {
            code: -32601,
            message: 'Method not found',
            data: {
              dsapErrorCode: 'METHOD_NOT_FOUND',
              reason: `Method '${request.method}' is not supported`,
              recommendations: [
                'Check the method name spelling',
                'Verify DSAP server capabilities',
                'Use one of: dsap.discover, dsap.verifyAccess, dsap.logActivity, dsap.checkCompliance, dsap.classifyData'
              ]
            }
          },
          id: request.id
        }
    }
  }

  private handleDiscover(request: DSAPRequest): DSAPResponse {
    return {
      jsonrpc: '2.0',
      result: this.agentCard,
      id: request.id
    }
  }

  private handleVerifyAccess(request: DSAPRequest): DSAPResponse {
    const { agentId, userId, dataResource, operation, context } = request.params
    const auditRecordId = `audit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    // Check user roles
    const userRoles = MOCK_USER_ROLES[userId] || []
    
    // Simulate policy decision logic
    const decision = this.simulateAccessDecision(agentId, userId, dataResource, operation, context, userRoles)

    return {
      jsonrpc: '2.0',
      result: decision,
      id: request.id
    }
  }

  private simulateAccessDecision(
    agentId: string,
    userId: string,
    dataResource: string,
    operation: string,
    context: any,
    userRoles: string[]
  ) {
    const auditRecordId = `audit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    
    // Determine access based on resource and user roles
    if (dataResource.includes('payroll') && !userRoles.includes('payroll_access')) {
      return {
        allowed: false,
        auditRecordId,
        policyReason: 'User lacks required role for payroll data access',
        riskScore: 95
      }
    }

    if (dataResource.includes('financial') && !userRoles.some(role => role.includes('finance'))) {
      return {
        allowed: false,
        auditRecordId,
        policyReason: 'User lacks required role for financial data access',
        riskScore: 90
      }
    }

    // Grant access with field-level restrictions
    const requestedFields = context.requestedFields || []
    const allowedFields: string[] = []
    const deniedFields: string[] = []

    requestedFields.forEach((field: string) => {
      if (field === 'ssn' && !userRoles.includes('pii_full_access')) {
        deniedFields.push(field)
      } else if (field === 'credit_score' && !userRoles.includes('financial_data_access')) {
        deniedFields.push(field)
      } else if (field === 'salary' && !userRoles.includes('payroll_access')) {
        deniedFields.push(field)
      } else {
        allowedFields.push(field)
      }
    })

    // Calculate risk score
    let riskScore = 20
    if (context.sensitivity?.includes('financial')) riskScore += 30
    if (context.sensitivity?.includes('PII')) riskScore += 20
    if (deniedFields.length > 0) riskScore += 15
    if (operation === 'write' || operation === 'delete') riskScore += 25

    return {
      allowed: true,
      auditRecordId,
      conditions: ['audit_required', 'time_limited'],
      restrictions: {
        allowedFields,
        deniedFields,
        maxRecords: allowedFields.length > 0 ? 100 : 0
      },
      ttl: 300, // 5 minutes
      policyReason: `Access granted with field restrictions based on user role: ${userRoles.join(', ')}`,
      policyVersion: 'v2.1.0',
      riskScore: Math.min(riskScore, 100)
    }
  }

  private handleLogActivity(request: DSAPRequest): DSAPResponse {
    const { agentId, userId, activity, auditRecordId } = request.params
    
    // Add to mock audit log
    auditLog.push({
      id: auditRecordId,
      timestamp: new Date().toISOString(),
      agentId,
      userId,
      resource: activity.resource,
      operation: activity.operation,
      outcome: 'success',
      riskScore: Math.floor(Math.random() * 100)
    })

    return {
      jsonrpc: '2.0',
      result: {
        logged: true,
        logEntryId: `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        complianceImpact: {
          regulationsAffected: ['GDPR', 'CCPA'],
          retentionRequired: 'P7Y',
          reportingRequired: false,
          alertsTriggered: []
        },
        timestamp: new Date().toISOString()
      },
      id: request.id
    }
  }

  private handleCheckCompliance(request: DSAPRequest): DSAPResponse {
    const { regulations, dataOperations } = request.params

    const findings = regulations.map((reg: string) => ({
      regulation: reg,
      status: 'compliant',
      requirements: this.getRegulationRequirements(reg),
      violations: [],
      score: Math.floor(Math.random() * 20) + 80 // 80-100
    }))

    return {
      jsonrpc: '2.0',
      result: {
        compliant: true,
        auditRecordId: `audit-compliance-${Date.now()}`,
        findings,
        requiredSafeguards: ['encryption_at_rest', 'audit_trail', 'access_control'],
        approvalRequired: false,
        riskAssessment: {
          overallRisk: 'low',
          riskScore: Math.floor(Math.random() * 30) + 10 // 10-40
        }
      },
      id: request.id
    }
  }

  private getRegulationRequirements(regulation: string): string[] {
    const requirements: Record<string, string[]> = {
      'GDPR': ['consent', 'data_minimization', 'encryption', 'right_to_erasure'],
      'CCPA': ['consumer_rights', 'opt_out', 'data_disclosure'],
      'HIPAA': ['access_control', 'audit_logs', 'encryption', 'minimum_necessary'],
      'SOX': ['audit_trail', 'access_control', 'data_integrity'],
      'PCI_DSS': ['encryption', 'access_control', 'network_security', 'monitoring']
    }
    return requirements[regulation] || ['access_control', 'audit_trail']
  }

  private handleClassifyData(request: DSAPRequest): DSAPResponse {
    const { dataResource } = request.params

    // Simulate data classification
    const classifications = []
    if (dataResource.includes('employee') || dataResource.includes('customer')) {
      classifications.push('PII')
    }
    if (dataResource.includes('payroll') || dataResource.includes('financial')) {
      classifications.push('financial')
    }
    if (dataResource.includes('medical') || dataResource.includes('health')) {
      classifications.push('health')
    }

    return {
      jsonrpc: '2.0',
      result: {
        resource: dataResource,
        classifications: classifications.length > 0 ? classifications : ['internal'],
        confidence: Math.random() * 0.3 + 0.7, // 70-100% confidence
        sensitivityLevel: classifications.includes('PII') || classifications.includes('financial') ? 'high' : 'medium',
        suggestedActions: [
          'Apply encryption at rest',
          'Enable audit logging',
          'Restrict access to authorized roles'
        ],
        complianceImplications: {
          regulations: ['GDPR', 'CCPA'],
          requirements: ['consent_required', 'audit_trail']
        }
      },
      id: request.id
    }
  }

  /**
   * Get recent audit log entries
   */
  getAuditLog(limit = 10) {
    return auditLog.slice(-limit).reverse()
  }

  /**
   * Get available scenarios
   */
  getScenarios() {
    return MOCK_SCENARIOS
  }

  /**
   * Reset mock state (for testing)
   */
  reset() {
    auditLog.length = 0
  }
}

// Singleton instance
export const mockServer = new DSAPMockServer()